import { useState, lazy, Suspense } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  Layers,
  BrainCircuit,
  FolderOpen,
  Server,
  Shield,
  PhoneCall,
  Sparkles,
  Bell,
  Settings,
  Users,
  Mail,
  Bot,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2 } from "lucide-react";
import { PlatformLayout } from "./platform-layout";
import { AiAgentsSurfaceProvider, PLATFORM_AI_AGENTS_HUB_SURFACE } from "@/pages/ai-agents-surface";

// Lazy-load each heavy sub-panel so the browser only parses + renders
// the active tab, not all 14 simultaneously.
const WorkspacesContent    = lazy(() => import("./platform-workspaces").then(m => ({ default: m.WorkspacesContent })));
const PlansHubContent      = lazy(() => import("./platform-plans-hub").then(m => ({ default: m.PlansHubContent })));
const AiIntelligenceContent= lazy(() => import("./platform-ai-help-tool").then(m => ({ default: m.AiIntelligenceContent })));
const ImportsContent       = lazy(() => import("./platform-imports-hub").then(m => ({ default: m.ImportsContent })));
const OperationsContent    = lazy(() => import("./platform-operations-hub").then(m => ({ default: m.OperationsContent })));
const SecurityHubContent   = lazy(() => import("./platform-security-hub").then(m => ({ default: m.SecurityHubContent })));
const CallsContent         = lazy(() => import("./platform-calls").then(m => ({ default: m.CallsContent })));
const ConfigHubContent     = lazy(() => import("./platform-config-hub").then(m => ({ default: m.ConfigHubContent })));
const NotificationSettings = lazy(() => import("./platform-notification-settings").then(m => ({ default: m.NotificationSettingsContent })));
const SettingsContent      = lazy(() => import("./platform-settings").then(m => ({ default: m.SettingsContent })));
const TeamContent          = lazy(() => import("./platform-team").then(m => ({ default: m.TeamContent })));
const EmailSettingsContent = lazy(() => import("./platform-email-settings").then(m => ({ default: m.EmailSettingsContent })));
const AiAssistantsContent  = lazy(() => import("./platform-ai-assistants").then(m => ({ default: m.AiAssistantsContent })));
const PlatformAiAgents     = lazy(() => import("./platform-ai-agents"));

interface AdminItem {
  key: string;
  label: string;
  icon: React.ElementType;
  badge?: "pii";
}

const adminItems: AdminItem[] = [
  { key: "team", label: "Team", icon: Users },
  { key: "workspaces", label: "Workspaces", icon: Building2 },
  { key: "plans", label: "Plans & Pricing", icon: Layers },
  { key: "email-settings", label: "Email Settings", icon: Mail },
  { key: "ai-agents", label: "AI Agents", icon: Sparkles },
  { key: "ai-intelligence", label: "AI Help Tool", icon: BrainCircuit },
  { key: "ai-assistants", label: "AI Assistants", icon: Bot },
  { key: "imports", label: "Migrations", icon: FolderOpen },
  { key: "operations", label: "Operations", icon: Server },
  { key: "security", label: "Security", icon: Shield, badge: "pii" },
  { key: "calls", label: "Calls", icon: PhoneCall },
  { key: "config", label: "Platform Config", icon: Sparkles },
  { key: "notifications", label: "Notifications", icon: Bell },
  { key: "settings", label: "Settings", icon: Settings },
];

function TabLoader() {
  return (
    <div className="flex items-center justify-center h-48 text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin" />
    </div>
  );
}

function ActivePanel({ selectedKey }: { selectedKey: string }) {
  switch (selectedKey) {
    case "team":
      return <TeamContent />;
    case "workspaces":
      return <div className="p-6 max-w-6xl mx-auto w-full"><WorkspacesContent /></div>;
    case "plans":
      return <div className="p-6 max-w-6xl mx-auto w-full"><PlansHubContent /></div>;
    case "email-settings":
      return <div className="p-6 max-w-6xl mx-auto w-full"><EmailSettingsContent /></div>;
    case "ai-agents":
      return (
        <div className="p-6 max-w-6xl mx-auto w-full">
          <AiAgentsSurfaceProvider value={PLATFORM_AI_AGENTS_HUB_SURFACE}>
            <PlatformAiAgents />
          </AiAgentsSurfaceProvider>
        </div>
      );
    case "ai-intelligence":
      return <AiIntelligenceContent />;
    case "ai-assistants":
      return <div className="p-6 max-w-6xl mx-auto w-full"><AiAssistantsContent /></div>;
    case "imports":
      return <div className="p-6 max-w-6xl mx-auto w-full"><ImportsContent /></div>;
    case "operations":
      return <div className="p-6 max-w-6xl mx-auto w-full"><OperationsContent /></div>;
    case "security":
      return <div className="p-6 max-w-6xl mx-auto w-full"><SecurityHubContent /></div>;
    case "calls":
      return <CallsContent />;
    case "config":
      return <div className="p-6 max-w-6xl mx-auto w-full"><ConfigHubContent /></div>;
    case "notifications":
      return <div className="p-6 max-w-6xl mx-auto w-full"><NotificationSettings /></div>;
    case "settings":
      return <div className="p-6 max-w-6xl mx-auto w-full"><SettingsContent /></div>;
    default:
      return null;
  }
}

export default function PlatformAdminHub() {
  const [selectedKey, setSelectedKey] = useState("workspaces");

  const { data: piiPending } = useQuery<{ count: number }>({
    queryKey: ["/api/platform/pii-scans/pending-count"],
    refetchInterval: 60000,
  });

  const piiCount = piiPending?.count ?? 0;

  return (
    <PlatformLayout fullBleed>
      <div className="flex h-full overflow-hidden">
        {/* Admin categories sidebar */}
        <div className="w-48 border-r bg-muted/20 flex flex-col shrink-0">
          <div className="px-3 pt-4 pb-2">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground px-1">
              Administration
            </p>
          </div>
          <ScrollArea className="flex-1">
            <nav className="px-2 pb-4 space-y-0.5">
              {adminItems.map((item) => {
                const Icon = item.icon;
                const isSelected = selectedKey === item.key;
                const hasBadge = item.badge === "pii" && piiCount > 0;
                return (
                  <button
                    key={item.key}
                    onClick={() => setSelectedKey(item.key)}
                    data-testid={`admin-nav-${item.key}`}
                    className={cn(
                      "w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-left transition-colors",
                      isSelected
                        ? "bg-primary text-primary-foreground"
                        : "text-foreground/70 hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <Icon className="h-3.5 w-3.5 shrink-0" />
                    <span className="flex-1 truncate text-sm">{item.label}</span>
                    {hasBadge && (
                      <Badge
                        className="bg-amber-500 text-black text-[10px] px-1 py-0 h-4 min-w-4 flex items-center justify-center"
                        data-testid={`badge-pii-${item.key}`}
                      >
                        {piiCount}
                      </Badge>
                    )}
                  </button>
                );
              })}
            </nav>
          </ScrollArea>
        </div>

        {/* Content pane — only the selected panel is mounted */}
        <div className="flex-1 overflow-y-auto">
          <Suspense fallback={<TabLoader />}>
            <ActivePanel selectedKey={selectedKey} />
          </Suspense>
        </div>
      </div>
    </PlatformLayout>
  );
}
